import type { AnalysisType, FDDQueueItem, FDDQueueItemStatus, PdfExtractScan } from "../types";

/**
 * The FDD queue is intentionally **session-only** (no localStorage). The queue
 * holds pointers to files that either live in the user's tab as `File` blobs
 * or in SharePoint at known drive/item ids. Both data sources die on reload —
 * the blob because it's tied to the page, the SharePoint refs because we'd
 * rather have a fresh "what did I pick this session" UX than ghost entries.
 */

type QueueMap = Record<string, FDDQueueItem[]>;

const queues: QueueMap = {};
const fileBlobs = new Map<string, File>();
/** PDF Extraction /scan results, by projectId. Same session-only lifetime as
 *  the queue: the panel unmounts on every tool switch, and without this the
 *  detected tables were lost and the user had to pay for a second scan. */
const pdfExtractScans = new Map<string, PdfExtractScan>();

export function getPdfExtractScan(projectId: string): PdfExtractScan | undefined {
  return pdfExtractScans.get(projectId);
}

export function setPdfExtractScan(projectId: string, scan: PdfExtractScan): void {
  pdfExtractScans.set(projectId, scan);
}

export function clearPdfExtractScan(projectId: string): void {
  pdfExtractScans.delete(projectId);
}

export function getFDDQueue(projectId: string): FDDQueueItem[] {
  return queues[projectId] || [];
}

export function getFDDQueueByType(projectId: string, analysisType: AnalysisType): FDDQueueItem[] {
  return getFDDQueue(projectId).filter((i) => i.analysisType === analysisType);
}

type AddInput = Omit<FDDQueueItem, "id" | "queuedAt" | "status"> & Partial<Pick<FDDQueueItem, "status">>;

export function addToQueue(projectId: string, item: AddInput, file?: File): FDDQueueItem {
  const queue = queues[projectId] || [];
  const newItem: FDDQueueItem = {
    ...item,
    id: crypto.randomUUID(),
    queuedAt: new Date().toISOString(),
    status: item.status || "pending",
  };
  queue.push(newItem);
  queues[projectId] = queue;
  if (file) fileBlobs.set(newItem.id, file);
  return newItem;
}

export function removeFromQueue(projectId: string, itemId: string): void {
  // Scan results cover every file that was queued, so they go with any one.
  if ((queues[projectId] || []).some((i) => i.id === itemId && i.analysisType === "pdf-extract")) {
    clearPdfExtractScan(projectId);
  }
  queues[projectId] = (queues[projectId] || []).filter((i) => i.id !== itemId);
  fileBlobs.delete(itemId);
}

export function clearQueueByType(projectId: string, analysisType: AnalysisType): void {
  const before = queues[projectId] || [];
  const removed = before.filter((i) => i.analysisType === analysisType);
  queues[projectId] = before.filter((i) => i.analysisType !== analysisType);
  for (const item of removed) fileBlobs.delete(item.id);
  if (analysisType === "pdf-extract") clearPdfExtractScan(projectId);
}

export function setItemStatus(
  projectId: string,
  itemId: string,
  status: FDDQueueItemStatus,
  patch?: Partial<Pick<FDDQueueItem, "completedAt" | "resultUrl" | "error">>,
): void {
  const queue = queues[projectId] || [];
  const idx = queue.findIndex((i) => i.id === itemId);
  if (idx === -1) return;
  queue[idx] = { ...queue[idx], status, ...(patch || {}) };
}

export function getFileBlob(itemId: string): File | undefined {
  return fileBlobs.get(itemId);
}

/**
 * After a VDR upload succeeds, stamp every matching `pending_upload` queue
 * item with the SharePoint refs so the FDD tools can fetch the file via Graph.
 *
 * Match is by exact filename within this project. Returns the number of items
 * promoted (0 if no pending upload was queued for that filename).
 */
export function promotePendingUploadByFileName(
  projectId: string,
  fileName: string,
  refs: { driveId: string; fileId: string; fileWebUrl: string; fileSize?: number },
): number {
  const queue = queues[projectId] || [];
  let promoted = 0;
  for (let i = 0; i < queue.length; i++) {
    const item = queue[i];
    if (item.status !== "pending_upload" || item.fileName !== fileName) continue;
    queue[i] = {
      ...item,
      status: "pending",
      driveId: refs.driveId,
      fileId: refs.fileId,
      fileWebUrl: refs.fileWebUrl,
      fileSize: refs.fileSize ?? item.fileSize,
    };
    promoted++;
  }
  return promoted;
}

/**
 * If the upload itself fails, mark the matching queue items as `error` so
 * the user can see the failure in the FDD toolbox without re-uploading.
 */
export function markPendingUploadFailed(
  projectId: string,
  fileName: string,
  errorMessage: string,
): number {
  const queue = queues[projectId] || [];
  let marked = 0;
  for (let i = 0; i < queue.length; i++) {
    const item = queue[i];
    if (item.status !== "pending_upload" || item.fileName !== fileName) continue;
    queue[i] = { ...item, status: "error", error: errorMessage };
    marked++;
  }
  return marked;
}
